import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Trash2, Upload, Info, ExternalLink } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { LoginGate } from "./LoginGate";
import { LockNotice } from "@/components/LockNotice";

const CONSENTS = [
  ["health_data", "Trattamento dei dati sanitari (necessario per caricare documenti)"],
  ["share_with_staff", "Condivisione dei documenti contrassegnati con i professionisti della clinica"],
  ["ai_assistant", "Uso dell'assistente AI per informazioni amministrative (nessun dato sanitario)"],
];

export function ConsentsCard() {
  const { reloadAccess } = useAuth();
  const [c, setC] = useState(null);
  useEffect(() => {
    api.get("/consents").then(({ data }) => setC(data));
  }, []);
  if (!c) return null;
  const toggle = async (k, v) => {
    const next = { health_data: c.health_data, share_with_staff: c.share_with_staff, ai_assistant: c.ai_assistant, [k]: v };
    const { data } = await api.put("/consents", next);
    setC(data);
    reloadAccess();
    toast.success("Consensi aggiornati");
  };
  return (
    <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid="consents-card">
      <p className="font-display text-sm font-bold text-slate-800">Gestione consensi</p>
      {CONSENTS.map(([k, label]) => (
        <label key={k} className="flex items-center justify-between gap-3 text-sm text-slate-600">
          {label}
          <Switch data-testid={`consent-${k}`} checked={!!c[k]} onCheckedChange={(v) => toggle(k, v)} />
        </label>
      ))}
    </div>
  );
}

async function download(d) {
  const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = d.filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function DocumentsPanel() {
  const { user, access } = useAuth();
  const uploadPolicy = access?.policies?.CARICA_DOCUMENTI;
  const [docs, setDocs] = useState([]);
  const [log, setLog] = useState([]);
  const [fse, setFse] = useState(null);
  const [share, setShare] = useState(false);
  const fileRef = useRef();
  const load = () => {
    api.get("/documents/mine").then(({ data }) => setDocs(data));
    api.get("/documents/access-log").then(({ data }) => setLog(data));
  };
  useEffect(() => {
    api.get("/fse/status").then(({ data }) => setFse(data));
    if (user) load();
  }, [user]);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", "referto");
    fd.append("share", share);
    try {
      await api.post("/documents", fd);
      toast.success("Documento caricato");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-5" data-testid="documents-panel">
      <div className="flex gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="fse-status">
        <Info className="h-4 w-4 shrink-0 text-sky-700" />
        <span><b>Archivio privato Doctor Selvix</b> – distinto dal Fascicolo Sanitario Elettronico nazionale. {fse?.message}</span>
      </div>
      <a href="https://www.fascicolosanitario.gov.it/portale/accedi-al-fascicolo" target="_blank" rel="noopener noreferrer" data-testid="fse-access-link"
        className="flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-emerald-700">
        Accedi al Fascicolo Sanitario <ExternalLink className="h-4 w-4" />
      </a>
      {!user ? <LoginGate text="Accedi per consultare il tuo archivio privato." /> : (
        <>
          <ConsentsCard />
          {uploadPolicy && !uploadPolicy.allowed ? <LockNotice policy={uploadPolicy} /> : <div className="rounded-2xl border border-dashed border-cyan-300 bg-cyan-50/50 p-4">
            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" data-testid="document-file-input" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-xs text-slate-600"><Switch data-testid="document-share-switch" checked={share} onCheckedChange={setShare} /> Condividi con i professionisti</label>
              <Button data-testid="document-upload-btn" onClick={() => fileRef.current.click()} className="rounded-full bg-sky-600 hover:bg-sky-700"><Upload className="mr-2 h-4 w-4" /> Carica PDF / immagine</Button>
            </div>
          </div>}
          <div className="space-y-2" data-testid="documents-list">
            {docs.length === 0 && <p className="text-sm text-slate-500">Nessun documento caricato.</p>}
            {docs.map((d) => (
              <div key={d.id} data-testid={`document-item-${d.id}`} className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3">
                <div className="min-w-0"><p className="truncate text-sm font-semibold text-slate-800">{d.filename}</p><p className="text-xs text-slate-500">{fmtDateTime(d.created_at)} · {(d.size / 1024).toFixed(0)} KB {d.shared_with_staff ? "· condiviso" : ""}</p></div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" data-testid={`document-download-${d.id}`} onClick={() => download(d).then(load)}><Download className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" data-testid={`document-delete-${d.id}`} onClick={() => api.delete(`/documents/${d.id}`).then(load)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                </div>
              </div>
            ))}
          </div>
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-widest text-cyan-700">Storico accessi</p>
            <div className="max-h-48 space-y-1 overflow-y-auto text-xs text-slate-600" data-testid="access-log">
              {log.map((l) => <p key={l.id}>{fmtDateTime(l.at)} · {l.action} · {l.filename} · {l.actor_name}</p>)}
              {!log.length && <p className="text-slate-400">Nessun accesso registrato.</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
